import { useState } from 'react'
import { AbrirRodadaSheet } from '../turno/AbrirRodadaSheet'
import { BarraDoTurno } from '../turno/BarraDoTurno'
import { ConnectionIndicator } from './ConnectionIndicator'
import { useTurnContextOptional, useChatContextOptional } from '../contexts'
import type { Player, TurnState } from '@/types/turn'

interface TopNavBarProps {
  // Props opcionais - se nao fornecidas, usa o contexto.
  // Usa o TurnState compartilhado: o tipo local era um subconjunto e nao
  // batia com o que o contexto entrega (dois erros de tipo antigos vinham dai).
  turnState?: TurnState | null
  currentPlayer?: Player | null
  permissions?: {
    canControlTurn: boolean
  }
  players?: Player[]
  onAdjustTime?: (seconds: number) => void
  onNextPlayer?: () => void
  onEndTurn?: () => void
  onStartTurn?: (config: { playerOrder: Player[], timeLimit: number }) => void
  onPauseTurn?: () => void
  formatTime?: (seconds: number) => string
  onStartCountdown?: () => void
  // Conexao
  isConnected?: boolean
  isReconnecting?: boolean
  // Nome da mesa (fase C, decisao do Paulo): exibido quando nao ha turno
  // ocupando a barra
  tableName?: string
  /**
   * Missao ativa, no formato "Missao 3: O laboratorio" (2026-07-27).
   *
   * Ocupa o lugar do nome da mesa quando existe: entre os dois, saber em que
   * capitulo a mesa esta vale mais do que o nome que ja esta no painel lateral
   * e na aba do navegador. Veio da `MissionSessionBar`, uma barra inteira
   * (~29px) so para esta linha de texto — na coluna do chat, que e o que mais
   * falta no celular.
   */
  missionTitle?: string
}

export default function TopNavBar(props: TopNavBarProps) {
  const [showTurnConfig, setShowTurnConfig] = useState(false)

  // Tentar usar contextos se disponiveis
  const turnContext = useTurnContextOptional()
  const chatContext = useChatContextOptional()

  // Conexao - usar props se fornecidas, senao usar contexto
  const isConnected = props.isConnected ?? chatContext?.isConnected ?? true
  const isReconnecting = props.isReconnecting ?? chatContext?.isReconnecting ?? false

  // Usar props se fornecidas, senao usar contexto
  const turnState = props.turnState ?? turnContext?.turnState
  const currentPlayer = props.currentPlayer ?? turnContext?.currentPlayer
  const permissions = props.permissions ?? turnContext?.permissions
  const players = props.players ?? turnContext?.players ?? []
  const formatTime = props.formatTime ?? turnContext?.formatTime
  const onStartTurn = props.onStartTurn ?? turnContext?.onStartTurn

  const handleTurnStart = (config: { playerOrder: Player[], timeLimit: number }) => {
    if (onStartTurn) {
      onStartTurn(config)
    }
    setShowTurnConfig(false)
  }

  // O titulo da missao tem precedencia sobre o nome da mesa: os dois disputam
  // o mesmo canto, e a missao diz em que ponto da campanha a mesa esta. Ambos
  // somem quando ha rodada correndo — ai a barra inteira e do relogio.
  const legenda = props.missionTitle || props.tableName

  return (
    <div className="bg-game-navbar-bg dark:bg-zinc-900/90 backdrop-blur-xl border-b border-game-border-primary dark:border-white/[0.05] h-12 md:h-14 flex items-center px-2 md:px-4 shadow-game-sm">
      {/* Indicador de conexao a esquerda - Desktop only (a logo migrou para o
          cabecalho do SidePanel, acima do nome da mesa) */}
      <div className="hidden md:flex items-center gap-3 mr-4 min-w-0 max-w-[40%]">
        <div className="flex-shrink-0">
          <ConnectionIndicator
            isConnected={isConnected}
            isReconnecting={isReconnecting}
          />
        </div>
        {/* No desktop so a missao sobe: o nome da mesa ja esta no cabecalho do
            SidePanel, a um palmo daqui. */}
        {props.missionTitle && !turnState?.isActive && (
          <span className="text-sm font-medium text-game-primary dark:text-zinc-100 truncate">
            {props.missionTitle}
          </span>
        )}
      </div>

      {/* Mobile: indicador de conexao + missao (ou nome da mesa). Some quando o
          turno precisa da barra inteira — o espaco e disputado. */}
      <div className="flex md:hidden items-center gap-2 mr-2 min-w-0">
        <div className="flex-shrink-0">
          <ConnectionIndicator
            isConnected={isConnected}
            isReconnecting={isReconnecting}
          />
        </div>
        {legenda && !turnState?.isActive && (
          <span className="text-sm font-medium text-game-primary dark:text-zinc-100 truncate">
            {legenda}
          </span>
        )}
      </div>

      {/* Conteudo central - flexivel. min-w-0 e essencial: sem ele este
          container nao encolhe abaixo do conteudo, o truncate do nome do
          jogador nunca age e os botoes de turno estouram da tela no mobile. */}
      <div className="flex-1 min-w-0 flex items-center justify-center overflow-hidden">
        {/* Os quatro comandos da rodada nao passam mais por aqui: viraram a
            ComandosDaRodada, na altura do polegar. O que fica e o relogio. */}
        <BarraDoTurno
          turnState={turnState}
          currentPlayer={currentPlayer}
          permissions={permissions}
          formatTime={formatTime}
          onOpenConfig={() => setShowTurnConfig(true)}
        />
      </div>

      {permissions?.canControlTurn && onStartTurn && (
        <AbrirRodadaSheet
          isOpen={showTurnConfig}
          onClose={() => setShowTurnConfig(false)}
          onStartTurn={handleTurnStart}
          players={players as Player[]}
          tableName={props.tableName}
        />
      )}
    </div>
  )
}
